import { TOUR_IDS } from "@bcc/components/tour/tour-ids";
import { Alert, AlertDescription } from "@bcc/components/ui/alert";
import { Button } from "@bcc/components/ui/button";
import { Input } from "@bcc/components/ui/input";
import { useToast } from "@bcc/hooks/use-toast";
import { capitalize } from "@client-shared/lib/string";
import { useProductsState } from "@client-shared/providers/products";
import { LoadingIcon } from "@bcc/components/ui/loading-icon/loading-icon";
import { Loader2, X } from "lucide-react";
import type { ChangeEvent } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type {
  CreateConnectionParams,
  FormProduct,
  GrindType,
  Installation,
  LocalisedProductConfig,
  Product,
  ProductConnection,
  Vendor,
} from "@shared/types";
import {
  findPreviousSelectedOptions,
  handleSelectValue,
  optionSelect,
  selectSyncOptions,
  setConnectedSourceObjectIds,
  setConnectedVariantIds,
  setFilteredConnections,
  setFilteredRemoteConnections,
  setFlatOptions,
  setMappedConnectionsCount,
  setPreconnectionToCreateConnectionParams,
  setSingleParentProducts,
  setSyncConnections,
  setUnmappedProducts,
} from "../product-sync-modal/product-sync-modal.connection.utils";
import { ProductSyncParentChildDisplay } from "../product-sync-modal/product-sync-parent-child-display";
import type { PreConnection, ProductSyncOption, SelectValue, VariantPreConnections } from "../product-sync-modal/product-sync-modal.types";
import { setProductSyncFooterCountTestId, setProductSyncModalTestId } from "../product-sync-modal/product-sync-modal.utils";
import { filterEligibleProducts, filterProductsBySearch, isVariantVisibleForSale } from "./product-sync.utils";

export interface ProductSyncProps {
  mode?: "modal" | "embedded";
  vendor: Vendor;
  installation: Installation;
  localProducts?: Product[];
  remoteProducts?: Product[];
  connections?: ProductConnection[];
  importedVariantIds?: string[];
  localizedProductConfig?: LocalisedProductConfig;
  remoteFormProducts: FormProduct[];
  submitConnections?: (connectionParams: CreateConnectionParams[], updatedConnections: ProductConnection[]) => Promise<boolean>;
  onCancel?: () => void;
  onSubmitSuccess?: () => void | Promise<void>;
  isLoading?: boolean;
  /**
   * When set, restricts the sync UI to this single product. Drives
   * "single product mode": eligibleProducts is filtered to just this
   * product, and `hideSearch` defaults to true (the search input is
   * redundant when there is only one product to map).
   */
  targetProduct?: Product;
  /**
   * Hides the search input when true. Defaults to true whenever
   * `targetProduct` is set, but can be passed explicitly to override.
   */
  hideSearch?: boolean;
}

export function ProductSync({
  mode = "modal",
  vendor,
  installation,
  importedVariantIds = [],
  localizedProductConfig,
  remoteFormProducts,
  submitConnections,
  onCancel,
  onSubmitSuccess,
  isLoading = false,
  targetProduct,
  hideSearch,
  ...props
}: ProductSyncProps) {
  // Default: hide search whenever we're operating on a single product.
  const shouldHideSearch = hideSearch ?? Boolean(targetProduct);
  const { t } = useTranslation();
  const { toast } = useToast();
  const [, , productsUtils] = useProductsState();
  const [connectionParams, setConnectionParams] = useState<CreateConnectionParams[]>([]);
  const [updatedConnections, setUpdatedConnections] = useState<ProductConnection[]>([]);
  const [loading, setLoading] = useState(false);
  const isSubmittingRef = useRef(false);
  const [connections, setConnections] = useState<ProductConnection[]>(props.connections ?? []);
  const [localProducts, setLocalProducts] = useState<Product[]>(props.localProducts ?? []);
  const [remoteProducts, setRemoteProducts] = useState<Product[]>(props.remoteProducts ?? []);
  const [searchQuery, setSearchQuery] = useState<string>("");

  const handleSearchChange = useCallback((e: ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value), []);

  const handleSearchClear = useCallback(() => setSearchQuery(""), []);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- sync local connections/products state from async-loaded props
    setConnections(props.connections ?? []);
    setLocalProducts(props.localProducts ?? []);
    setRemoteProducts(props.remoteProducts ?? []);
  }, [props.connections, props.localProducts, props.remoteProducts]);

  const reset = useCallback(() => {
    setUpdatedConnections([]);
    setConnectionParams([]);
  }, []);

  // eslint-disable-next-line react-hooks/preserve-manual-memoization -- manual memoization preserved until React Compiler is enabled across the workspace
  const handleSubmit = useCallback(async () => {
    if (isSubmittingRef.current) {
      return;
    }
    isSubmittingRef.current = true;
    try {
      setLoading(true);
      const submitCallback = submitConnections ?? productsUtils?.handleSyncProductConnections;

      if (!submitCallback) {
        throw new Error("Missing product sync submit callback");
      }

      const hasSubmitted = await submitCallback(connectionParams, updatedConnections);

      if (!hasSubmitted) {
        throw new Error("Failed to sync product connections");
      }

      toast({
        variant: "success",
        title: t("components.ProductSyncModal.productSynced", {
          defaultValue: "Products synced",
        }),
      });
      reset();
      await onSubmitSuccess?.();
    } catch (error) {
      console.error("Failed to sync product connections:", error);
      toast({
        title: t("common.error", { defaultValue: "Error" }),
        description: t("components.ProductSyncModal.submitError", {
          defaultValue: "Unable to save mappings. Please check your data and try again.",
        }),
        variant: "destructive",
      });
    } finally {
      isSubmittingRef.current = false;
      setLoading(false);
    }
  }, [
    connectionParams,
    onSubmitSuccess,
    productsUtils?.handleSyncProductConnections,
    reset,
    submitConnections,
    t,
    toast,
    updatedConnections,
  ]);

  const handleSync = useCallback(
    (newConnectionParams: CreateConnectionParams[], deselectedValues: string[]) => {
      setSyncConnections(connectionParams, setConnectionParams, setUpdatedConnections, connections)(newConnectionParams, deselectedValues);
    },
    [connectionParams, connections]
  );

  const eligibleProducts = useMemo(() => {
    if (targetProduct) {
      // Single-product / remap mode: skip the webStatus eligibility filter.
      // Orphan rows synthesised from PAUSED connections have no webStatus on
      // their variants, so filterEligibleProducts would wrongly exclude them.
      return localProducts.filter((product) => product.id === targetProduct.id);
    }
    return filterEligibleProducts(localProducts);
  }, [localProducts, targetProduct]);

  const searchFilteredProducts = useMemo(() => filterProductsBySearch(eligibleProducts, searchQuery), [eligibleProducts, searchQuery]);

  const filteredVariantConnections = useMemo(() => setFilteredConnections(connections) ?? [], [connections]);

  const connectedRemoteVariantIds = useMemo(() => setConnectedVariantIds(filteredVariantConnections) ?? [], [filteredVariantConnections]);

  const connectedSourceObjectIds = useMemo(
    () => setConnectedSourceObjectIds(filteredVariantConnections) ?? [],
    [filteredVariantConnections]
  );

  const filteredRemoteProducts = useMemo(() => {
    return setFilteredRemoteConnections(remoteProducts, connectedRemoteVariantIds);
  }, [connectedRemoteVariantIds, remoteProducts]);

  const isSaveDisabled = useMemo(() => {
    return !(connectionParams.length > 0 || updatedConnections.length > 0);
  }, [connectionParams.length, updatedConnections.length]);

  const requiredSourceObjectIds = useMemo(
    () => [
      ...new Set(
        eligibleProducts
          .flatMap((product) => product.variants ?? [])
          .filter((variant) => Boolean(targetProduct) || isVariantVisibleForSale(variant))
          .map((variant) => variant.id)
      ),
    ],
    [eligibleProducts, targetProduct]
  );

  const mappedCount = useMemo(
    () => setMappedConnectionsCount(requiredSourceObjectIds, connectionParams, updatedConnections, filteredVariantConnections),
    [connectionParams, requiredSourceObjectIds, updatedConnections, filteredVariantConnections]
  );

  const allParentChildProducts = useMemo(() => {
    if (searchFilteredProducts.length === 0) {
      return { connectedAllLocalProducts: [], unConnectedAllLocalProducts: [] };
    }
    const all = setSingleParentProducts(searchFilteredProducts);
    const unConnectedAllLocalProducts = setUnmappedProducts(all as Product[], connectedSourceObjectIds);
    const connectedAllLocalProducts = all?.filter((product) => {
      return (
        unConnectedAllLocalProducts.find((unmapped) => {
          return unmapped.id === product.id;
        }) === undefined
      );
    });

    return { all, connectedAllLocalProducts, unConnectedAllLocalProducts };
  }, [searchFilteredProducts, connectedSourceObjectIds]);

  const handleSetPreconnectionToConnection = useCallback(
    (preConnection: PreConnection, grindType: GrindType): CreateConnectionParams => {
      return setPreconnectionToCreateConnectionParams(vendor.vendorKey, installation.id)(preConnection, grindType);
    },
    [installation.id, vendor.vendorKey]
  );

  return (
    <div className={mode === "embedded" ? "space-y-4" : "space-y-3"}>
      <Alert>
        <AlertDescription>
          {t("components.ProductSyncModal.introduction", {
            defaultValue: "Select and map your product variants from {{service}} into Beanz.",
            service: capitalize(installation.serviceId),
          })}
        </AlertDescription>
      </Alert>

      {!isLoading && !shouldHideSearch && (
        <div className="relative">
          <Input
            placeholder={t("products.productSync.searchPlaceholder", {
              defaultValue: "Search products...",
            })}
            value={searchQuery}
            onChange={handleSearchChange}
            testId={setProductSyncModalTestId("search-input")}
          />
          {searchQuery !== "" && (
            <button
              type="button"
              onClick={handleSearchClear}
              data-testid={setProductSyncModalTestId("search-clear-button")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              aria-label={t("products.productSync.clearSearch", {
                defaultValue: "Clear search",
              })}
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
          )}
        </div>
      )}

      <div className={mode === "modal" ? "max-h-[58vh] overflow-y-auto pr-2" : "max-h-[62vh] overflow-y-auto pr-2"}>
        {isLoading ? (
          <div data-testid={setProductSyncModalTestId("loading-state")} className="flex items-center justify-center py-12">
            <LoadingIcon />
          </div>
        ) : (
          <ProductSyncSelect
            filteredVariantConnections={filteredVariantConnections}
            unmappedProducts={allParentChildProducts.unConnectedAllLocalProducts as Product[]}
            mappedProducts={allParentChildProducts.connectedAllLocalProducts as Product[]}
            connectionParams={connectionParams}
            updatedConnections={updatedConnections}
            filteredRemoteProducts={filteredRemoteProducts}
            localProducts={localProducts}
            handleSync={handleSync}
            importedVariantIds={importedVariantIds}
            localizedProductConfig={localizedProductConfig}
            allRemoteFormProducts={remoteFormProducts}
            handleSetPreconnectionToConnection={handleSetPreconnectionToConnection}
          />
        )}
      </div>

      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <Button variant="secondary" disabled className="w-full sm:w-auto" testId={setProductSyncFooterCountTestId("required-products")}>
          {t("components.ProductSelectModal.requiredProducts", {
            defaultValue: "{{mapped}} / {{count}} required products mapped",
            count: requiredSourceObjectIds.length,
            mapped: mappedCount,
          })}
        </Button>
        <div className="flex w-full gap-2 sm:w-auto">
          {mode === "modal" && (
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                reset();
                onCancel?.();
              }}
              className="flex-1 sm:flex-none"
              testId={setProductSyncModalTestId("cancel-button")}
            >
              {t("buttons.cancel", { defaultValue: "Cancel" })}
            </Button>
          )}
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={isSaveDisabled || loading || isLoading}
            className="flex-1 sm:flex-none"
            testId={setProductSyncModalTestId("continue-button")}
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            {t("products.productSync.mapProducts", {
              defaultValue: "Map Products",
            })}
          </Button>
        </div>
      </div>
    </div>
  );
}

interface ProductSyncSelectProps {
  filteredVariantConnections: ProductConnection[];
  localProducts: Product[];
  allRemoteFormProducts: FormProduct[];
  mappedProducts: Product[];
  unmappedProducts: Product[];
  connectionParams: CreateConnectionParams[];
  updatedConnections: ProductConnection[];
  filteredRemoteProducts: Product[];
  importedVariantIds?: string[];
  handleSync: (connectionParams: CreateConnectionParams[], deselectedValues: string[]) => void;
  localizedProductConfig?: LocalisedProductConfig;
  handleSetPreconnectionToConnection: (preConnection: PreConnection, grindType: GrindType) => CreateConnectionParams;
}

function ProductSyncSelect({
  filteredVariantConnections,
  allRemoteFormProducts,
  localProducts,
  unmappedProducts,
  mappedProducts,
  connectionParams,
  updatedConnections,
  filteredRemoteProducts,
  importedVariantIds = [],
  handleSync,
  localizedProductConfig,
  handleSetPreconnectionToConnection,
}: ProductSyncSelectProps) {
  const { t } = useTranslation();

  const options = useMemo(() => {
    return setFlatOptions(
      filteredRemoteProducts,
      importedVariantIds,
      t("components.ProductSyncModal.variantIsImported", {
        defaultValue: "(already imported)",
      })
    );
  }, [filteredRemoteProducts, importedVariantIds, t]);

  const [availableOptions, setAvailableOptions] = useState<ProductSyncOption[]>(options);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- sync available options when the source options prop changes
    setAvailableOptions(options);
  }, [options]);

  const handleFindPreviousSelectedOptions = useCallback(
    (remoteVariantIds: string[]) => {
      return findPreviousSelectedOptions(connectionParams, updatedConnections, availableOptions)(remoteVariantIds);
    },
    [connectionParams, updatedConnections, availableOptions]
  );

  const handleOptions = useCallback(
    (values: SelectValue[] | SelectValue, deselectedValues: SelectValue[] | SelectValue, previousSelectedOptions?: ProductSyncOption[]) => {
      optionSelect(setAvailableOptions)(values, deselectedValues, previousSelectedOptions);
    },
    []
  );

  const getRemoteFormProduct = useCallback(
    (targetVariantId: string) => {
      return allRemoteFormProducts.find((product) => {
        return product.variants?.find((variant) => variant.id === targetVariantId);
      });
    },
    [allRemoteFormProducts]
  );

  const handleSelect = useCallback(
    (variantPreConnections: VariantPreConnections) => {
      const { allCreateConnectionParams, selectedValues, deselectedValues } =
        handleSelectValue(localProducts, handleSetPreconnectionToConnection)(variantPreConnections) ?? {};
      if (!allCreateConnectionParams || allCreateConnectionParams.length === 0) {
        return selectSyncOptions(handleSync, handleFindPreviousSelectedOptions, handleOptions)(
          [],
          deselectedValues ?? [],
          selectedValues ?? []
        );
      }
      selectSyncOptions(handleSync, handleFindPreviousSelectedOptions, handleOptions)(allCreateConnectionParams, [], []);
    },
    [handleFindPreviousSelectedOptions, handleOptions, handleSetPreconnectionToConnection, handleSync, localProducts]
  );

  const handleClear = useCallback(
    (targetVariantIds: string[]) => {
      selectSyncOptions(handleSync, handleFindPreviousSelectedOptions, handleOptions)(connectionParams, targetVariantIds ?? [], []);
    },
    [connectionParams, handleFindPreviousSelectedOptions, handleOptions, handleSync]
  );

  const noAvailablePublishedProducts = useMemo(() => {
    return t("components.ProductSyncModal.noAvailableProducts", {
      defaultValue: "No available products",
    });
  }, [t]);

  return (
    <div className="flex flex-col gap-2" data-tour-id={TOUR_IDS.products.syncList}>
      {mappedProducts.length > 0 &&
        mappedProducts.map((product) => {
          return (
            <ProductSyncParentChildDisplay
              key={product.id}
              getRemoteFormProduct={getRemoteFormProduct}
              localProduct={product}
              grinds={localizedProductConfig?.grind ?? []}
              options={availableOptions}
              handleSelect={handleSelect}
              noAvailablePublishedProducts={noAvailablePublishedProducts}
              connections={filteredVariantConnections}
              onClear={handleClear}
            />
          );
        })}
      {unmappedProducts.length > 0 &&
        unmappedProducts.map((product) => {
          return (
            <ProductSyncParentChildDisplay
              key={product.id}
              getRemoteFormProduct={getRemoteFormProduct}
              localProduct={product}
              grinds={localizedProductConfig?.grind ?? []}
              options={availableOptions}
              handleSelect={handleSelect}
              noAvailablePublishedProducts={noAvailablePublishedProducts}
              connections={filteredVariantConnections}
              onClear={handleClear}
            />
          );
        })}
    </div>
  );
}
